/**
 * The widget's whole client state, as one hook.
 *
 * Owns: the visitor session, the message list, socket wiring, unread counts,
 * the mute preference, and the emergency override that force-opens the panel.
 */
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'preact/hooks';
import { SERVER_EVENTS } from '@superchat/shared/events';
import { createApi } from './api.js';
import { createRealtime } from './realtime.js';
import { playMessageTone, playEmergencyTone, primeAudio } from './sound.js';
import {
  enablePush,
  showLocalNotification,
  requestNotificationPermission,
  notificationsSupported,
} from './push.js';
import { storageKeys, storage } from './config.js';
import { uploadFile, attachmentUrl } from './upload.js';
import { collectContext, trackNavigation } from './context.js';
import { isWithinHours, nextOpenLabel } from './hours.js';

const initial = {
  phase: 'booting', // booting | ready | failed
  error: null,
  open: false,
  view: 'prechat', // prechat | chat | offline | doc
  session: null,
  app: null,
  conversation: null,
  messages: [],
  cursor: null,
  hasMore: false,
  loadingMore: false,
  unread: 0,
  agentTyping: false,
  connection: 'connecting',
  muted: false,
  // The agent alert that forced the panel open, once one lands. Nothing is
  // rendered from it any more — it only routes the panel to the thread.
  emergency: null,
  // Whether the visitor waved away the "where can we reach you?" card. Seeded
  // from storage at boot, so the full card is only ever asked once.
  emailPromptDismissed: false,
  docs: [],
  activeDoc: null,
  sending: false,
  // Whether this server can take a file at all, answered by /session so the
  // paperclip never appears on an install that has nowhere to put one.
  uploads: { enabled: false, maxBytes: 0 },
  pushReady: false,
  // 'default' | 'granted' | 'denied' | 'unsupported'
  notifyPermission: 'default',
  diagnostic: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'booted': {
      const { session, messages, cursor, hasMore, muted, offline, emailDismissed } = action;
      const hasHistory = messages.length > 0;
      return {
        ...state,
        phase: 'ready',
        session,
        app: session.app,
        conversation: session.conversation,
        messages,
        cursor,
        hasMore,
        muted,
        emailPromptDismissed: Boolean(emailDismissed),
        unread: session.conversation?.unread?.visitor ?? 0,
        uploads: session.uploads ?? state.uploads,
        view: hasHistory ? 'chat' : offline ? 'offline' : 'prechat',
      };
    }
    case 'failed':
      return { ...state, phase: 'failed', error: action.error };

    case 'email:prompt':
      return { ...state, emailPromptDismissed: action.dismissed };

    case 'open':
      return { ...state, open: true, unread: 0 };
    case 'close':
      return { ...state, open: false, agentTyping: false };
    case 'view':
      return { ...state, view: action.view, activeDoc: action.doc ?? null };

    case 'messages:prepend':
      return {
        ...state,
        messages: [...action.messages, ...state.messages],
        cursor: action.cursor,
        hasMore: action.hasMore,
        loadingMore: false,
      };
    case 'messages:loading':
      return { ...state, loadingMore: true };

    case 'message:add': {
      const { message, countUnread } = action;
      // De-dupe against the optimistic copy (matched on clientId) and against
      // a socket event that races the POST response.
      const without = state.messages.filter(
        (m) =>
          m.id !== message.id &&
          !(message.clientId && m.clientId && m.clientId === message.clientId)
      );
      return {
        ...state,
        messages: [...without, message].sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        ),
        view: state.view === 'prechat' || state.view === 'offline' ? 'chat' : state.view,
        unread: countUnread ? state.unread + 1 : state.unread,
        agentTyping: message.senderType === 'agent' ? false : state.agentTyping,
        emergency: message.isEmergency ? message : state.emergency,
      };
    }
    /**
     * An agent rewrote or withdrew something they had already sent.
     *
     * Replaced in place rather than added: the row is already on screen, the
     * visitor may well have read it, and appending would show the correction
     * as a new arrival. Unknown ids are ignored — an edit to a message this
     * session never loaded is nothing to reconcile.
     */
    case 'message:patch':
      return {
        ...state,
        messages: state.messages.map((m) => (m.id === action.message.id ? action.message : m)),
        // A withdrawn user alert stops holding the panel hostage.
        emergency:
          state.emergency?.id === action.message.id && action.message.deleted
            ? null
            : state.emergency,
      };

    case 'message:failed':
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.clientId === action.clientId ? { ...m, failed: true, pending: false } : m
        ),
      };
    case 'messages:read':
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.senderType === 'visitor' && !m.readAt ? { ...m, readAt: action.readAt } : m
        ),
      };

    case 'typing':
      return { ...state, agentTyping: action.isTyping };
    case 'connection':
      return { ...state, connection: action.status };
    case 'conversation':
      return { ...state, conversation: action.conversation };
    case 'muted':
      return { ...state, muted: action.muted };
    case 'sending':
      return { ...state, sending: action.sending };
    case 'unread:clear':
      return { ...state, unread: 0 };
    case 'emergency:ack':
      return { ...state, emergency: null };
    case 'docs':
      return { ...state, docs: action.docs };
    case 'push:ready':
      return { ...state, pushReady: true };
    case 'notify:permission':
      return { ...state, notifyPermission: action.permission };
    case 'diagnostic':
      return { ...state, diagnostic: action.notice };
    case 'diagnostic:clear':
      return { ...state, diagnostic: null };
    default:
      return state;
  }
}

let clientSeq = 0;
const nextClientId = () =>
  `w_${Date.now().toString(36)}_${(clientSeq += 1)}_${Math.random().toString(36).slice(2, 8)}`;

export function useSuperchat(config) {
  const [state, dispatch] = useReducer(reducer, initial);
  const keys = useMemo(() => storageKeys(config.publicKey), [config.publicKey]);
  const api = useMemo(
    () => createApi({ apiBase: config.apiBase, publicKey: config.publicKey }),
    [config.apiBase, config.publicKey]
  );

  const rt = useRef(null);
  const vapidKey = useRef(null);
  // Set once the browser has actually answered — granted or denied. A browser
  // that refused to show the prompt leaves this false so a gesture can retry.
  const notifyAsked = useRef(false);
  // A prompt is on screen. Without this, a click while it is open would fire a
  // second request behind the first.
  const notifyAsking = useRef(false);
  const reauthing = useRef(false);
  // Read inside socket handlers without making the brand a dependency.
  const brandRef = useRef(config.brand || 'Support');
  const stopTracking = useRef(null);

  /** Trades the public key for a fresh visitor token and rejoins the room. */
  const reauthenticate = useCallback(async () => {
    if (reauthing.current) return;
    reauthing.current = true;
    try {
      const session = await api.session({
        visitorId: storage.get(keys.visitorId) ?? undefined,
        visitor: config.visitor,
      });
      storage.set(keys.visitorId, session.visitorId);
      rt.current?.connect(session.token);
      rt.current?.subscribe(session.conversation.id);

      // The server starts a new session when the IP has changed since the
      // last one, so the thread on screen may no longer be ours to show.
      if (session.conversation.id !== live.current.conversationId) {
        const { messages, meta } = await api.messages({ limit: 30 });
        dispatch({
          type: 'booted',
          session,
          messages: [...messages].reverse(),
          cursor: meta?.nextCursor ?? null,
          hasMore: Boolean(meta?.hasMore),
          muted: live.current.muted,
          offline: !isWithinHours(config.hours),
          emailDismissed: storage.get(keys.emailDismissed) === 'true',
        });
      }
    } catch (err) {
      if (config.debug) console.warn('[superchat] re-authentication failed', err);
    } finally {
      reauthing.current = false;
    }
  }, [api, keys.visitorId, keys.emailDismissed, config.visitor, config.hours, config.debug]);
  // Read inside socket handlers without re-subscribing on every state change.
  const live = useRef({ open: false, muted: false, conversationId: null });
  live.current.open = state.open;
  live.current.muted = state.muted;
  live.current.conversationId = state.conversation?.id ?? null;

  const offline = useMemo(() => !isWithinHours(config.hours), [config.hours]);

  /**
   * Ask for notification permission, then subscribe to Web Push if the merchant
   * hosts a worker.
   *
   * Called at boot without waiting for a gesture: a merchant who never opens
   * the panel still has to be reachable, and being reachable when they are not
   * looking is the entire point. Firefox and Safari will not prompt outside a
   * user-generated event, so `requestNotificationPermission` reports that as
   * null and `open()` retries from the click.
   *
   * Permission is requested even when there is no worker path. Without one
   * there is no push subscription, but the permission alone still buys
   * foreground notifications for a backgrounded tab, which is the common case.
   */
  const armNotifications = useCallback(async () => {
    if (notifyAsked.current || notifyAsking.current) return;

    if (!notificationsSupported()) {
      notifyAsked.current = true;
      dispatch({ type: 'notify:permission', permission: 'unsupported' });
      return;
    }

    notifyAsking.current = true;
    try {
      const permission = await requestNotificationPermission({ debug: config.debug });

      // The prompt never appeared. Leave the flag down so the next gesture can
      // try again, and do not report a permission the visitor never saw.
      if (permission === null) return;

      notifyAsked.current = true;
      dispatch({ type: 'notify:permission', permission });
      if (config.debug) console.info('[superchat] notification permission:', permission);
      if (permission !== 'granted') return;

      if (!config.serviceWorkerPath || !vapidKey.current) return;
      const subscription = await enablePush({
        api,
        serviceWorkerPath: config.serviceWorkerPath,
        vapidPublicKey: vapidKey.current,
        debug: config.debug,
      });
      if (subscription) dispatch({ type: 'push:ready' });
    } finally {
      notifyAsking.current = false;
    }
  }, [api, config.serviceWorkerPath, config.debug]);

  /* ------------------------------------------------------------------ boot */

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const storedVisitorId = storage.get(keys.visitorId) ?? undefined;
        const muted = storage.get(keys.muted) === 'true';
        const emailDismissed = storage.get(keys.emailDismissed) === 'true';

        const session = await api.session({
          visitorId: storedVisitorId,
          visitor: config.visitor,
          context: collectContext({ topUrl: config.topUrl }),
        });
        if (cancelled) return;

        storage.set(keys.visitorId, session.visitorId);
        brandRef.current = config.brand || session.app?.name || 'Support';

        const { messages, meta } = await api.messages({ limit: 30 });
        if (cancelled) return;

        // The API paginates backwards (newest first); display is chronological.
        const ordered = [...messages].reverse();

        dispatch({
          type: 'booted',
          session,
          messages: ordered,
          cursor: meta?.nextCursor ?? null,
          hasMore: Boolean(meta?.hasMore),
          muted,
          offline,
          emailDismissed,
        });

        const landed = ordered.filter((m) => m.isEmergency && m.senderType === 'agent');
        if (landed.length > 0) {
          // An emergency that arrived while the widget was closed still forces
          // the panel open on the next load.
          dispatch({ type: 'message:add', message: landed[landed.length - 1] });
          dispatch({ type: 'open' });
        }

        /* realtime */
        rt.current = createRealtime({
          apiBase: config.apiBase,
          debug: config.debug,
          onStatus: (status) => {
            dispatch({ type: 'connection', status });
            // Visitor tokens last 12h and there is no refresh endpoint for
            // them — a rejected handshake means re-minting the session from
            // the public key, which is cheap and idempotent for a known
            // visitorId.
            if (status === 'unauthorized') reauthenticate();
          },
          onEvent: handleEvent,
        });
        rt.current.connect(session.token);
        rt.current.subscribe(session.conversation.id);

        // Follow the merchant around their admin. Reported as it happens, so
        // an agent opening the thread sees where they actually are now.
        stopTracking.current = trackNavigation((page) => {
          api.activity([page]).catch(() => {
            // A dropped page view is not worth surfacing to the merchant.
          });
        });

        vapidKey.current = session.vapidPublicKey;

        // Ask immediately, with no interaction required. Browsers that decline
        // to prompt this early are handled inside armNotifications.
        armNotifications();
      } catch (error) {
        if (!cancelled) dispatch({ type: 'failed', error });
      }
    })();

    return () => {
      cancelled = true;
      rt.current?.disconnect();
      // Restores the patched history methods.
      stopTracking.current?.();
    };
    // Boot once per app. config is resolved before mount and never mutates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Last resort for browsers that only prompt from a user gesture: the first
   * interaction anywhere on the page, not just with the widget. A merchant who
   * never touches the launcher still gets asked. The listeners unbind as soon
   * as the browser has answered, which the permission change re-runs this for.
   */
  useEffect(() => {
    if (notifyAsked.current) return undefined;

    const retry = () => armNotifications();
    const options = { capture: true, passive: true };
    document.addEventListener('pointerdown', retry, options);
    document.addEventListener('keydown', retry, options);
    return () => {
      document.removeEventListener('pointerdown', retry, options);
      document.removeEventListener('keydown', retry, options);
    };
  }, [armNotifications, state.notifyPermission]);

  /* -------------------------------------------------------------- events */

  const handleEvent = useCallback(
    (event, payload) => {
      if (event === SERVER_EVENTS.MESSAGE_NEW) {
        if (payload.conversationId !== live.current.conversationId) return;
        // Our own echo is already on screen from the optimistic send.
        if (payload.senderType === 'visitor') {
          dispatch({ type: 'message:add', message: payload });
          return;
        }

        const closed = !live.current.open;
        dispatch({ type: 'message:add', message: payload, countUnread: closed });

        // The server skips its push while a socket is attached, so a
        // backgrounded tab would otherwise hear nothing at all.
        showLocalNotification({
          // The same title either way. A user alert is not a category the
          // visitor knows about, and labelling one in their notification tray
          // is the loudest possible way to tell them.
          title: brandRef.current,
          body: payload.body.slice(0, 140),
          tag: `superchat:${payload.conversationId}`,
          // Still passed: this only controls `renotify`/`requireInteraction`,
          // which is the notification surviving long enough to be seen — not a
          // visible label. Forcing the panel open and then letting the
          // notification vanish unnoticed defeats the point.
          isEmergency: payload.isEmergency,
        });

        if (payload.isEmergency) {
          /**
           * Force the panel open, and sound it as an ordinary message.
           *
           * The open is the whole mechanism now — it is what an agent is
           * choosing when they send one. The distinct alarm tone is gone with
           * the rest of the visitor-facing treatment: a chat that opens itself
           * playing a siren is not "an ordinary reply arrived", whatever the
           * bubble says. It plays over a mute, because a panel opening in
           * silence is worse than a chime.
           */
          dispatch({ type: 'open' });
          playMessageTone();
        } else if (!live.current.muted) {
          playMessageTone();
        }
        return;
      }

      /**
       * Silent by design: no tone, no notification, no panel opening. An edit
       * is a correction to something the visitor was already told about, and
       * a withdrawal is the agent taking it back — interrupting them for
       * either would be the opposite of what both are for.
       */
      if (event === SERVER_EVENTS.MESSAGE_UPDATED) {
        if (payload.conversationId !== live.current.conversationId) return;
        dispatch({ type: 'message:patch', message: payload });
        return;
      }

      if (event === SERVER_EVENTS.MESSAGE_READ) {
        if (payload.readerSide === 'agent') {
          dispatch({ type: 'messages:read', readAt: payload.readAt });
        }
        return;
      }

      if (event === SERVER_EVENTS.TYPING) {
        if (payload.senderType === 'agent') {
          dispatch({ type: 'typing', isTyping: Boolean(payload.isTyping) });
        }
        return;
      }

      if (event === SERVER_EVENTS.CONVERSATION_UPDATED) {
        if (payload.id === live.current.conversationId) {
          dispatch({ type: 'conversation', conversation: payload });
        }
        return;
      }

      /**
       * A broadcast test from an agent. Behaves like the real thing so the
       * test is meaningful: the alert variant force-opens the panel over any
       * mute, an ordinary one just chimes.
       */
      if (event === SERVER_EVENTS.DIAGNOSTIC_PING) {
        if (payload.direction !== 'to-visitors') return;

        dispatch({
          type: 'diagnostic',
          notice: 'Test notification from support — delivered live.',
        });

        if (payload.isEmergency) {
          dispatch({ type: 'open' });
          playMessageTone();
        } else if (!live.current.muted) {
          playMessageTone();
        }
      }
    },
    []
  );

  /* ------------------------------------------------------------- actions */

  const open = useCallback(() => {
    // This click is the gesture that unlocks WebAudio, and the retry window for
    // browsers that refused to prompt at boot without one.
    primeAudio();
    armNotifications();
    // Only when the visitor opened it themselves. A panel forced open by an
    // agent's own alert is not the visitor reaching for support, and reporting
    // it as one would ring that agent's console back at them.
    if (!live.current.open) rt.current?.announceOpen();
    dispatch({ type: 'open' });
  }, [armNotifications]);

  const close = useCallback(() => dispatch({ type: 'close' }), []);

  const setView = useCallback((view, doc) => dispatch({ type: 'view', view, doc }), []);

  const toggleMute = useCallback(() => {
    const muted = !live.current.muted;
    storage.set(keys.muted, String(muted));
    dispatch({ type: 'muted', muted });
  }, [keys.muted]);

  const send = useCallback(
    async ({ body, attachments = [] }) => {
      const text = (body ?? '').trim();
      if (!text && attachments.length === 0) return;

      const clientId = nextClientId();
      const optimistic = {
        id: clientId,
        clientId,
        conversationId: live.current.conversationId,
        senderType: 'visitor',
        senderName: '',
        body: text,
        attachments,
        isEmergency: false,
        readAt: null,
        createdAt: new Date().toISOString(),
        pending: true,
      };

      dispatch({ type: 'message:add', message: optimistic });
      dispatch({ type: 'sending', sending: true });
      rt.current?.setTyping(false);

      try {
        // clientId makes the send idempotent: a retry returns the original
        // message rather than posting a duplicate.
        const saved = await api.send({ body: text, attachments, clientId });
        dispatch({ type: 'message:add', message: saved });
      } catch (error) {
        dispatch({ type: 'message:failed', clientId });
        if (config.debug) console.warn('[superchat] send failed', error);
      } finally {
        dispatch({ type: 'sending', sending: false });
      }
    },
    [api, config.debug]
  );

  const retry = useCallback(
    (message) => send({ body: message.body, attachments: message.attachments }),
    [send]
  );

  const loadMore = useCallback(async () => {
    if (!state.hasMore || state.loadingMore || !state.cursor) return;
    dispatch({ type: 'messages:loading' });
    try {
      const { messages, meta } = await api.messages({ cursor: state.cursor, limit: 30 });
      dispatch({
        type: 'messages:prepend',
        messages: [...messages].reverse(),
        cursor: meta?.nextCursor ?? null,
        hasMore: Boolean(meta?.hasMore),
      });
    } catch {
      dispatch({ type: 'messages:prepend', messages: [], cursor: state.cursor, hasMore: false });
    }
  }, [api, state.cursor, state.hasMore, state.loadingMore]);

  const setTyping = useCallback((isTyping) => rt.current?.setTyping(isTyping), []);

  const updateProfile = useCallback(
    async (profile) => {
      const conversation = await api.updateProfile(profile);
      dispatch({ type: 'conversation', conversation });
      return conversation;
    },
    [api]
  );

  /**
   * The email prompt, in its three states.
   *
   * Saving hides it for good — the card only renders while
   * `conversation.visitor.email` is empty, and `updateProfile` refreshes that.
   * Dismissing is remembered across reloads so nobody is asked twice, but only
   * demotes it to the one-line version; reopening is deliberately *not*
   * persisted, so a visitor who expands it and changes their mind is back to
   * the quiet state on the next load rather than being asked again.
   */
  const saveEmail = useCallback((email) => updateProfile({ email }), [updateProfile]);

  const dismissEmailPrompt = useCallback(() => {
    storage.set(keys.emailDismissed, 'true');
    dispatch({ type: 'email:prompt', dismissed: true });
  }, [keys.emailDismissed]);

  const reopenEmailPrompt = useCallback(
    () => dispatch({ type: 'email:prompt', dismissed: false }),
    []
  );

  const searchDocs = useCallback(
    async (q) => {
      try {
        const docs = await api.docs({ q, limit: 5 });
        dispatch({ type: 'docs', docs });
      } catch {
        dispatch({ type: 'docs', docs: [] });
      }
    },
    [api]
  );

  const openDoc = useCallback(
    async (slug) => {
      try {
        const doc = await api.doc(slug);
        dispatch({ type: 'view', view: 'doc', doc });
      } catch {
        /* the article was unpublished between listing and opening */
      }
    },
    [api]
  );

  /* Mark read whenever the panel is open and the thread has unread inbound. */
  useEffect(() => {
    if (!state.open || state.phase !== 'ready') return;
    const unreadInbound = state.messages.some((m) => m.senderType === 'agent' && !m.readAt);
    if (!unreadInbound && !state.conversation?.unread?.visitor) return;

    api.markRead().catch(() => {});
    dispatch({ type: 'unread:clear' });
  }, [state.open, state.phase, state.messages, state.conversation, api]);

  /* A diagnostic notice is transient — it must not linger as if it were state. */
  useEffect(() => {
    if (!state.diagnostic) return undefined;
    const timer = setTimeout(() => dispatch({ type: 'diagnostic:clear' }), 8000);
    return () => clearTimeout(timer);
  }, [state.diagnostic]);

  /* Suggested articles, fetched once, so the pre-chat form can deflect. */
  useEffect(() => {
    if (state.phase === 'ready' && state.view === 'prechat' && state.docs.length === 0) {
      searchDocs();
    }
  }, [state.phase, state.view, state.docs.length, searchDocs]);

  return {
    ...state,
    offline,
    nextOpen: useMemo(() => nextOpenLabel(config.hours), [config.hours]),
    accent: config.accent ?? state.app?.settings?.accentColor ?? '#6E5BFF',
    brand: config.brand || state.app?.name || 'Support',
    welcome: state.app?.settings?.welcomeMessage ?? 'Hi! How can we help?',
    actions: {
      open,
      close,
      setView,
      send,
      retry,
      loadMore,
      setTyping,
      toggleMute,
      updateProfile,
      saveEmail,
      dismissEmailPrompt,
      reopenEmailPrompt,
      searchDocs,
      openDoc,
      notifyAgents: (isEmergency) => api.notifyAgents(isEmergency),
      /**
       * Uploads bypass the reducer entirely: a file in flight belongs to the
       * composer that picked it, and putting per-file progress in global state
       * would re-render the whole panel on every chunk.
       */
      // `config.debug` rides along rather than being read inside upload.js:
      // the widget runs on someone else's page and must stay silent there
      // unless the snippet asked for tracing with `data-debug="true"`.
      upload: (file, options) => uploadFile(api, file, { ...options, debug: config.debug }),
      attachmentUrl: (attachment, options) =>
        attachmentUrl(api, attachment, { ...options, debug: config.debug }),
      /**
       * Lets the host app supply the exact top-level URL once it knows it.
       * Inside an embedded Shopify app the browser will not reveal the admin
       * URL to us, so the app itself is the only source of truth.
       */
      setTopUrl: (topUrl) => api.activity([], topUrl),
      // Local only, on purpose: isolates the WebAudio path from delivery.
      testTone: () => (live.current.muted ? playEmergencyTone() : playMessageTone()),
    },
  };
}

export default useSuperchat;
